import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import {
  PipelineItem,
  DeckRenderMode,
  PipelineSection,
  SECTION_COLORS,
  SECTION_LABELS,
  SECTION_ORDER,
  PageLayout,
} from '../types';
import { DeckPage, DeckFooter, CANVAS_W, CANVAS_H, useDeckAutoPan } from '../components/DeckPage';
import { CARD_HEADER_LETTER_SPACING } from '../components/DeckCard';
import { AdjustableMapLegend, DeckLegendCaption, DeckMap, mapLegendHeight, mapRectHeight, projectToCanvas } from '../components/DeckMap';
import {
  formatEstLaunchPrice,
  formatLandCost,
  formatTargetPreview,
  normalizeProjectName,
  normalizeEstLaunchPrice,
  normalizeLandCost,
  normalizeTargetPreview,
} from '../utils/displayFormat';
import { clusterMarkerPlacements, hasFiniteCoordinates, markerTextLayout, resolveMarkerTextCollisions } from '../utils/markerPlacement';
import { handlePlainDrop, handlePlainLineBreak, handlePlainPaste, handlePlainTextInput, normalizeEditableText } from '../utils/editableText';
import { comparePipelineItems, projectNameOf, roadNameOf } from '../utils/projectOrdering';

const LEFT_SECTIONS: PipelineSection[] = ['NORTH', 'NORTH_EAST', 'CENTRAL'];
const RIGHT_SECTIONS: PipelineSection[] = ['WEST', 'EAST', 'EC', 'COMMERCIAL_INDUSTRIAL'];
// Keep the map clear of the footer/disclaimer. Saved layouts remain
// authoritative; this only affects a newly generated overview.
const MAP_RECT = { x: 431, y: 352, w: 858 };
const OVERVIEW_PLACEHOLDER = { x: 1260, y: 730, w: 330 };
// Keep the visible title layout unchanged. This separate blocker follows the
// rendered title glyphs rather than the full-width layout box, allowing
// generated tables to approach the title more closely.
const OVERVIEW_TITLE_LAYOUT_RECT = { x: 500, y: 130, w: 600, h: 120 };
const OVERVIEW_TITLE_BLOCKER = { x: 595, y: 130, w: 410, h: 114 };
const DEFAULT_CAPTION = { x: 10, y: 816 };
const DEFAULT_CAPTION_WIDTH = 440;
const DEFAULT_LEGEND = {
  x: OVERVIEW_PLACEHOLDER.x - 18 - 220,
  y: OVERVIEW_PLACEHOLDER.y,
  w: 220,
};
const OVERVIEW_PLACEHOLDER_H = mapLegendHeight(DEFAULT_LEGEND.w, true);
const DEFAULT_TABLE_GAP = 8;
const MARKER_COLLISION_PADDING = 0;
const NEUTRAL_ROW_COLOR = '#EAEDEA';
const RESIDENTIAL_SECTIONS: PipelineSection[] = ['NORTH', 'NORTH_EAST', 'CENTRAL', 'EAST', 'WEST'];

type FrameCallback<T> = (value: T) => void;

function scheduleFrame<T>(frameRef: React.MutableRefObject<number | null>, valueRef: React.MutableRefObject<T | null>, value: T, callback: FrameCallback<T>) {
  valueRef.current = value;
  if (frameRef.current != null) return;
  frameRef.current = window.requestAnimationFrame(() => {
    frameRef.current = null;
    const next = valueRef.current;
    valueRef.current = null;
    if (next != null) callback(next);
  });
}

function cancelFrame(frameRef: React.MutableRefObject<number | null>) {
  if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current);
  frameRef.current = null;
}
// Overview markers use one consistent diameter, including clustered markers.
// Keep every Overview marker the same compact size, regardless of project
// number or cluster membership. Text scales within this fixed circle.
const PROJECT_MARKER_RADIUS = 8;

type CanvasArea = { x: number; y: number; w: number; h: number };
type TableResizeCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
type OverviewLayout = PageLayout & { columnWidths?: Record<string, number> };

function areasOverlap(a: CanvasArea, b: CanvasArea, padding = 0): boolean {
  return a.x < b.x + b.w + padding
    && a.x + a.w > b.x - padding
    && a.y < b.y + b.h + padding
    && a.y + a.h > b.y - padding;
}

function fitsCanvas(area: CanvasArea): boolean {
  return area.x >= 0 && area.y >= 0
    && area.x + area.w <= CANVAS_W
    && area.y + area.h <= CANVAS_H;
}

/** Find a nearby free position for a newly generated table. */
function findSafeTablePosition(
  preferred: CanvasArea,
  blocked: CanvasArea[],
  occupied: CanvasArea[],
): CanvasArea {
  // Prefer preserving the selected left/right column, then search the canvas
  // in a small grid so a table never starts under the title or a marker. If a
  // very large table cannot fit, reduce its height proportionally until a
  // safe placement is available. The caller passes this height through to
  // the draggable table, so the initial layout remains collision-free.
  const step = 8;
  const minimumHeight = Math.min(preferred.h, 180);
  const isAvailable = (candidate: CanvasArea) => fitsCanvas(candidate)
    && !blocked.some((area) => areasOverlap(candidate, area, MARKER_COLLISION_PADDING))
    && !occupied.some((area) => areasOverlap(candidate, area, DEFAULT_TABLE_GAP / 2));

  for (let height = preferred.h; height >= minimumHeight; height = Math.max(minimumHeight, height - 8)) {
    const shape = { ...preferred, h: height };
    const preferredColumn = [shape];
    for (let y = 10; y <= CANVAS_H - shape.h; y += step) preferredColumn.push({ ...shape, y });
    const columnMatch = preferredColumn.find(isAvailable);
    if (columnMatch) return columnMatch;
    for (let x = 10; x <= CANVAS_W - shape.w; x += step) {
      for (let y = 10; y <= CANVAS_H - shape.h; y += step) {
        const candidate = { ...shape, x, y };
        if (isAvailable(candidate)) return candidate;
      }
    }
    if (height === minimumHeight) break;
  }

  // There is no free rectangle at the minimum readable height. Keep the
  // preferred coordinates, but clamp the height to the canvas boundary rather
  // than allowing it to extend into the footer.
  return { ...preferred, h: Math.min(preferred.h, CANVAS_H - preferred.y) };
}

type OverviewColumnKey = 'num' | 'project' | keyof PipelineItem;

const COLS: Array<{ key: OverviewColumnKey; label: string; w: number }> = [
  { key: 'num', label: '', w: 16 },
  { key: 'project', label: 'Project', w: 120 },
  { key: 'district', label: 'District', w: 26 },
  { key: 'marketRegion', label: 'Region', w: 28 },
  { key: 'tenure', label: 'Tenure', w: 46 },
  { key: 'estUnits', label: 'Est. # Units', w: 34 },
  { key: 'developer', label: 'Developer', w: 82 },
  { key: 'landCost', label: 'Est. Land Cost', w: 38 },
  { key: 'estLaunchPrice', label: 'Est. Avg Launch Price', w: 42 },
  { key: 'targetPreview', label: 'Target Preview', w: 44 },
];
const OVERVIEW_HEADER_LINES: Partial<Record<OverviewColumnKey, string[]>> = {
  estUnits: ['Est. #', 'Units'],
  landCost: ['Est. Land', 'Cost'],
  estLaunchPrice: ['Est. Avg', 'Launch Price'],
  targetPreview: ['Target', 'Preview'],
};
const TABLE_W = COLS.reduce((s, c) => s + c.w, 0) + 8;
const OVERVIEW_HEADER_HORIZONTAL_PADDING = 5;
const OVERVIEW_HEADER_DIVIDER_CLEARANCE = 2;
// Minimum logical widths keep each explicitly authored header segment on one
// line, so every header remains at most two lines while the total table width
// stays fixed during column resizing.
const OVERVIEW_COLUMN_MIN_WIDTHS: Record<string, number> = {
  num: 16,
  project: 110,
  district: 32,
  marketRegion: 34,
  tenure: 40,
  estUnits: 30,
  developer: 78,
  landCost: 34,
  estLaunchPrice: 50,
  targetPreview: 40,
};

function statusOf(item: PipelineItem): string {
  const status = item.status?.trim().toUpperCase() || '';
  return status === 'NONE' ? '' : status;
}

function displayLandCost(item: PipelineItem): string {
  const value = formatLandCost(item.landCost);
  return value === 'NIL' ? value : `${value} psf ppr`;
}

/**
 * The project column is a composed cell rather than a single stored field.
 * Keep its visible line order stable so editing the cell does not hide the
 * status, road name, remarks, or award note.
 */
function projectCellLines(item: PipelineItem): string[] {
  return [
    statusOf(item),
    projectNameOf(item) ? `${projectNameOf(item)}${item.hasProjectName ? '\u00a0*' : ''}` : '',
    roadNameOf(item),
    projectNameOf(item) && item.remarks?.trim() ? item.remarks.trim() : '',
    !item.landAwarded ? '*The Land Has Not Been Awarded' : '',
  ].filter(Boolean);
}

function projectCellText(item: PipelineItem): string {
  return projectCellLines(item).join('\n');
}

function parseProjectCellText(text: string, item: PipelineItem): PipelineItem {
  const lines = normalizeEditableText(text)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const hadStatus = Boolean(statusOf(item));
  const hadProjectName = Boolean(projectNameOf(item));
  const hadRoadName = Boolean(roadNameOf(item));
  const hadRemarks = Boolean(item.remarks?.trim());
  const hasAwardNote = lines.some((line) => /land\s+has\s+not\s+been\s+awarded/i.test(line));
  const content = lines.filter((line) => !/land\s+has\s+not\s+been\s+awarded/i.test(line));
  let index = 0;
  const next = () => content[index++] ?? '';
  const nextStatus = hadStatus ? next() : statusOf(item);
  const nextProject = hadProjectName ? normalizeProjectName(next()) : null;
  const nextRoad = hadRoadName ? next() : roadNameOf(item);
  const nextRemarks = hadRemarks ? content.slice(index).join(' ').trim() : item.remarks;
  return {
    ...item,
    status: nextStatus || null,
    projectName: nextProject,
    roadName: nextRoad || null,
    remarks: nextRemarks || null,
    landAwarded: !hasAwardNote,
    hasProjectName: Boolean(nextProject),
  };
}

function regionColorKey(section: PipelineSection): string {
  return section === 'NORTH' || section === 'NORTH_EAST' ? 'color:NORTH_GROUP' : `color:${section}`;
}

function regionColorOf(layout: PageLayout, section: PipelineSection): string | undefined {
  const shared = layout['color:NORTH_GROUP']?.color;
  if (section === 'NORTH' || section === 'NORTH_EAST') {
    return shared || layout[`color:${section}`]?.color;
  }
  return layout[regionColorKey(section)]?.color;
}

/** A readable table-row tint derived from a manually selected header colour. */
function areaRowTint(color: string): string {
  const match = color.trim().match(/^#([0-9a-f]{6})$/i);
  if (!match) return 'rgba(31, 42, 68, 0.08)';
  const value = match[1];
  const red = Number.parseInt(value.slice(0, 2), 16);
  const green = Number.parseInt(value.slice(2, 4), 16);
  const blue = Number.parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, 0.14)`;
}

type OverviewMarker = {
  item: PipelineItem;
  number: number;
  x: number;
  y: number;
  itemIds?: number[];
  numbers?: number[];
};

type PositionedOverviewMarker = OverviewMarker & { radius: number };

type OverviewResolvedColumn = (typeof COLS[number]) & { w: number };

function resolveOverviewColumns(
  sourceItems: PipelineItem[],
  numbers: Map<number, number>,
  savedColumnWidths?: Record<string, number>,
): OverviewResolvedColumn[] {
  const bounds: Record<string, { min: number; max: number }> = {
    num: { min: OVERVIEW_COLUMN_MIN_WIDTHS.num, max: 16 }, project: { min: OVERVIEW_COLUMN_MIN_WIDTHS.project, max: 190 },
    district: { min: OVERVIEW_COLUMN_MIN_WIDTHS.district, max: 42 }, marketRegion: { min: OVERVIEW_COLUMN_MIN_WIDTHS.marketRegion, max: 44 }, tenure: { min: OVERVIEW_COLUMN_MIN_WIDTHS.tenure, max: 72 },
    estUnits: { min: OVERVIEW_COLUMN_MIN_WIDTHS.estUnits, max: 50 }, developer: { min: OVERVIEW_COLUMN_MIN_WIDTHS.developer, max: 155 },
    landCost: { min: OVERVIEW_COLUMN_MIN_WIDTHS.landCost, max: 62 }, estLaunchPrice: { min: OVERVIEW_COLUMN_MIN_WIDTHS.estLaunchPrice, max: 72 },
    targetPreview: { min: OVERVIEW_COLUMN_MIN_WIDTHS.targetPreview, max: 68 },
  };
  const desired = COLS.map((column) => {
    const values = sourceItems.map((item) => {
      if (column.key === 'num') return String(numbers.get(item.id) ?? '');
      if (column.key === 'project') {
        return [statusOf(item), `${projectNameOf(item)}${item.hasProjectName && projectNameOf(item) ? '\u00a0*' : ''}`, roadNameOf(item), !item.landAwarded ? 'The land has not been awarded' : ''].filter(Boolean).join(' ');
      }
      const raw = item[column.key as keyof PipelineItem];
      if (column.key === 'targetPreview') return formatTargetPreview(item.targetPreview);
      if (column.key === 'landCost') return displayLandCost(item);
      if (column.key === 'estLaunchPrice') return formatEstLaunchPrice(item.estLaunchPrice);
      return raw == null ? '' : String(raw);
    });
    const headerLines = OVERVIEW_HEADER_LINES[column.key] ?? [column.label];
    const longestHeaderSegment = Math.max(...headerLines.map((line) => line.length));
    const longest = Math.max(longestHeaderSegment, ...values.map((value) => value.length));
    const limit = bounds[String(column.key)];
    const safetyWidth = OVERVIEW_HEADER_HORIZONTAL_PADDING * 2 + OVERVIEW_HEADER_DIVIDER_CLEARANCE;
    return { ...column, desired: Math.min(limit.max, Math.max(limit.min, 8 + longest * 3.25 + safetyWidth)) };
  });
  const fixed = desired.find((column) => column.key === 'num')?.desired ?? 16;
  const flexibleTotal = desired.filter((column) => column.key !== 'num').reduce((sum, column) => sum + column.desired, 0);
  const available = TABLE_W - fixed;
  const automatic = desired.map((column) => ({
    ...column,
    w: column.key === 'num' ? fixed : column.desired / flexibleTotal * available,
  }));
  const restored = savedColumnWidths ? automatic.map((column) => ({
    ...column,
    w: savedColumnWidths[String(column.key)] != null
      ? savedColumnWidths[String(column.key)] / 100 * TABLE_W
      : column.w,
  })) : automatic;
  const clamped = restored.map((column) => ({
    ...column,
    w: Math.max(column.w, bounds[String(column.key)]?.min ?? 0),
  }));
  const total = clamped.reduce((sum, column) => sum + column.w, 0);
  if (total <= TABLE_W) {
    return clamped.map((column) => ({ ...column, w: column.w / total * TABLE_W }));
  }
  const shrinkable = clamped.reduce(
    (sum, column) => sum + Math.max(0, column.w - (bounds[String(column.key)]?.min ?? 0)),
    0,
  );
  if (!shrinkable) return clamped;
  const excess = total - TABLE_W;
  return clamped.map((column) => {
    const minimum = bounds[String(column.key)]?.min ?? 0;
    const availableWidth = Math.max(0, column.w - minimum);
    return { ...column, w: column.w - excess * (availableWidth / shrinkable) };
  });
}

const SectionTable: React.FC<{
  section: PipelineSection;
  items: PipelineItem[];
  numbers: Map<number, number>;
  editable?: boolean;
  editingItemId?: number | null;
  onItemChange?: (item: PipelineItem) => void;
  onEditItem?: (itemId: number) => void;
  savedColumnWidths?: Record<string, number>;
  sharedColumnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  onColumnWidthsCommit?: (widths: Record<string, number>) => void;
  onColumnWidthsCancel?: () => void;
  headerColor?: string;
}> = ({
  section, items, numbers, editable = false, editingItemId, onItemChange, onEditItem,
  savedColumnWidths, sharedColumnWidths, onColumnWidthsChange, onColumnWidthsCommit, onColumnWidthsCancel, headerColor,
}) => {
  const colors = SECTION_COLORS[section];
  const areaColor = headerColor ?? colors.bg;
  const areaTint = headerColor ? areaRowTint(areaColor) : colors.light;
  const residentialHeader = RESIDENTIAL_SECTIONS.includes(section);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const columnDrag = useRef<{ index: number; startX: number; scale: number; widths: number[]; latest?: Record<string, number> } | null>(null);
  const columnFrame = useRef<number | null>(null);
  const columnFrameValue = useRef<Record<string, number> | null>(null);
  const columns = useMemo(() => sharedColumnWidths
    ? COLS.map((column) => ({ ...column, w: sharedColumnWidths[String(column.key)] / 100 * TABLE_W }))
    : resolveOverviewColumns(items, numbers, savedColumnWidths),
  [items, numbers, savedColumnWidths, sharedColumnWidths]);
  if (items.length === 0) return null;
  return (
    <Box ref={tableRef} sx={{ mb: '4px', backgroundColor: NEUTRAL_ROW_COLOR, borderRadius: '3px', overflow: 'hidden', border: '0.5px solid #000000' }}>
      <Box sx={{ display: 'flex', backgroundColor: areaColor, color: '#ffffff' }}>
        {columns.map((c, columnIndex) => (
          <Box
            key={String(c.key)}
            data-overview-header="true"
            sx={{
              position: 'relative',
              width: c.w,
              minWidth: 0,
              flexShrink: 0,
              boxSizing: 'border-box',
              fontSize: 6,
              fontWeight: 400,
              lineHeight: 1.15,
              px: `${OVERVIEW_HEADER_HORIZONTAL_PADDING}px`,
              paddingTop: '2px',
              paddingBottom: '2px',
              minHeight: '19px',
              borderBottom: '0.5px solid #000000',
              borderRight: '0.5px solid #000000',
              textAlign: 'center',
              whiteSpace: c.key === 'district' || c.key === 'marketRegion' ? 'nowrap' : 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
              overflowWrap: 'normal',
              wordBreak: 'normal',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'block',
                overflow: 'visible',
                width: '100%',
                whiteSpace: c.key === 'district' || c.key === 'marketRegion' ? 'nowrap' : 'normal',
                overflowWrap: 'normal',
                wordBreak: 'normal',
                ...(c.key === 'marketRegion' ? {
                  fontSize: 'calc(6px / var(--overview-table-scale-x))',
                  transform: 'scaleY(calc(var(--overview-table-scale-x) / var(--overview-table-scale-y)))',
                  transformOrigin: 'center',
                } : {}),
              }}
            >
              {(OVERVIEW_HEADER_LINES[c.key] ?? [c.label]).map((line, index) => (
                <React.Fragment key={`${String(c.key)}-${index}`}>
                  {index > 0 && <br />}
                <span style={{ whiteSpace: 'nowrap' }}>{line}</span>
                </React.Fragment>
              ))}
            </Box>
            {editable && columnIndex < columns.length - 1 && (
              <Box
                title="Drag to resize columns; double-click to reset automatic widths"
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  cancelFrame(columnFrame);
                  columnDrag.current = null;
                  onColumnWidthsCommit?.({});
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  const renderedWidth = tableRef.current?.getBoundingClientRect().width ?? TABLE_W;
                  columnDrag.current = {
                    index: columnIndex,
                    startX: event.clientX,
                    scale: renderedWidth / TABLE_W,
                    widths: columns.map((column) => column.w),
                  };
                  tableRef.current?.style.setProperty('will-change', 'width, height, transform');
                  event.currentTarget.setPointerCapture(event.pointerId);
                  event.preventDefault();
                }}
                onPointerMove={(event) => {
                  const current = columnDrag.current;
                  if (!current) return;
                  const leftColumn = columns[current.index];
                  const rightColumn = columns[current.index + 1];
                  const leftMinimum = OVERVIEW_COLUMN_MIN_WIDTHS[leftColumn.key];
                  const rightMinimum = OVERVIEW_COLUMN_MIN_WIDTHS[rightColumn.key];
                  const requested = (event.clientX - current.startX) / current.scale;
                  const delta = Math.max(
                    leftMinimum - current.widths[current.index],
                    Math.min(requested, current.widths[current.index + 1] - rightMinimum),
                  );
                  const widths = [...current.widths];
                  widths[current.index] += delta;
                  widths[current.index + 1] -= delta;
                  const nextWidths = Object.fromEntries(
                    columns.map((column, index) => [String(column.key), widths[index] / TABLE_W * 100]),
                  );
                  current.latest = nextWidths;
                  scheduleFrame(columnFrame, columnFrameValue, nextWidths, (next) => onColumnWidthsChange?.(next));
                }}
                onPointerUp={() => {
                  const current = columnDrag.current;
                  cancelFrame(columnFrame);
                  if (current?.latest) onColumnWidthsCommit?.(current.latest);
                  else onColumnWidthsCancel?.();
                  columnDrag.current = null;
                  tableRef.current?.style.removeProperty('will-change');
                }}
                onPointerCancel={() => {
                  cancelFrame(columnFrame);
                  columnDrag.current = null;
                  onColumnWidthsCancel?.();
                  tableRef.current?.style.removeProperty('will-change');
                }}
                sx={{
                  position: 'absolute', top: 0, right: -3, bottom: 0, width: 7,
                  cursor: 'col-resize', zIndex: 2, touchAction: 'none',
                }}
              />
            )}
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          backgroundColor: areaTint,
          color: '#1f2a44',
          fontSize: 9,
          fontWeight: 900,
          textAlign: 'center',
          letterSpacing: residentialHeader ? CARD_HEADER_LETTER_SPACING : 0.6,
          py: '2px',
          borderTop: '0.5px solid #000000',
          borderBottom: '0.5px solid #000000',
          borderLeft: 'none',
          borderRight: 'none',
        }}
      >
        {SECTION_LABELS[section]}
      </Box>
      {items.map((item, idx) => (
        <Box
          key={item.id}
          data-pipeline-item-id={item.id}
          sx={{
            display: 'flex',
            backgroundColor: idx % 2 ? areaTint : NEUTRAL_ROW_COLOR,
            borderTop: '0.5px solid #000000',
            alignItems: 'stretch',
          }}
        >
          {columns.map((c) => {
            let value: React.ReactNode = '–';
            let extra: object = {};
            const rowBackground = idx % 2 ? areaTint : NEUTRAL_ROW_COLOR;
            if (c.key === 'num') {
              value = numbers.get(item.id) ?? '';
              extra = { fontWeight: 400, backgroundColor: rowBackground };
            } else if (c.key === 'project') {
              value = editingItemId === item.id
                ? projectCellText(item)
                : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', lineHeight: 1.15, textAlign: 'center' }}>
                    {statusOf(item) && <Box component="span" sx={{ fontSize: 5, fontWeight: 400 }}>{statusOf(item)}</Box>}
                    {projectNameOf(item) ? <Box component="span" sx={{ fontSize: 7, fontWeight: 900 }}>{projectNameOf(item)}{item.hasProjectName ? '\u00a0*' : ''}</Box> : null}
                    {roadNameOf(item) && <Box component="span" sx={{ fontSize: projectNameOf(item) ? 6 : 7, fontStyle: projectNameOf(item) ? 'italic' : 'normal', fontWeight: projectNameOf(item) ? 400 : 900 }}>{roadNameOf(item)}</Box>}
                    {item.remarks?.trim() && <Box component="span" sx={{ fontSize: projectNameOf(item) ? 6 : 7, fontStyle: 'italic', fontWeight: 400 }}>{item.remarks.trim()}</Box>}
                    {!item.landAwarded && <Box component="span" sx={{ fontSize: 5, color: '#d32f2f', fontStyle: 'italic', fontWeight: 400 }}>*The Land Has Not Been Awarded</Box>}
                  </Box>
                );
              extra = { textAlign: 'center', fontWeight: 900 };
            } else if (c.key === 'targetPreview') {
              value = formatTargetPreview(item.targetPreview);
              extra = { color: item.isLaunchingSoon ? '#e01f1f' : '#1f2a44', fontWeight: 400 };
            } else if (c.key === 'estLaunchPrice') {
              value = formatEstLaunchPrice(item.estLaunchPrice);
              extra = { fontWeight: 900 };
            } else {
              const raw = item[c.key as keyof PipelineItem];
              if (c.key === 'landCost') value = displayLandCost(item);
              else value = raw == null || raw === '' ? '–' : String(raw);
            }
            return (
              <Box
                key={String(c.key)}
                data-editable-table-cell={c.key === 'num' ? undefined : 'true'}
                sx={{
                  width: c.w,
                  minWidth: 0,
                  flexShrink: 0,
                  boxSizing: 'border-box',
                  fontSize: 7,
                  fontWeight: 400,
                  color: '#1f2a44',
                  px: '3px',
                  py: '1.5px',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  whiteSpace: editingItemId === item.id && c.key !== 'num' ? 'pre-line' : 'normal',
                  overflowWrap: 'break-word',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '0.5px solid #000000',
                  borderBottom: '0.5px solid #000000',
                  ...extra,
                }}
                contentEditable={editingItemId === item.id && c.key !== 'num'}
                suppressContentEditableWarning
                onPointerDown={(event) => {
                  if (editingItemId === item.id && c.key !== 'num') event.stopPropagation();
                }}
                onKeyDown={handlePlainLineBreak}
                onBeforeInput={handlePlainTextInput}
                onPaste={handlePlainPaste}
                onDrop={handlePlainDrop}
                onDoubleClick={(event) => {
                  if (!editable || c.key === 'num') return;
                  event.preventDefault();
                  event.stopPropagation();
                  const cell = event.currentTarget;
                  onEditItem?.(item.id);
                  requestAnimationFrame(() => {
                    cell.focus();
                    const selection = window.getSelection();
                    if (!selection) return;
                    const range = document.createRange();
                    range.selectNodeContents(cell);
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);
                  });
                }}
                onBlur={(event) => {
                  if (editingItemId !== item.id || c.key === 'num') return;
                  const text = normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent).trim();
                  const nextValue = c.key === 'project'
                    ? parseProjectCellText(text, item)
                    : c.key === 'targetPreview'
                      ? normalizeTargetPreview(text)
                      : c.key === 'landCost'
                        ? normalizeLandCost(text)
                        : c.key === 'estLaunchPrice'
                          ? normalizeEstLaunchPrice(text)
                          : text === '–' || !text ? null : text;
                  onItemChange?.(c.key === 'project'
                    ? nextValue as PipelineItem
                    : { ...item, [c.key]: nextValue });
                }}
              >
                {c.key === 'num' ? (
                  <Box
                    component="span"
                    sx={{
                      width: 14, height: 14, aspectRatio: 1, flex: '0 0 14px', boxSizing: 'border-box',
                      borderRadius: '50%',
                      border: '0.5px solid #000000', bgcolor: rowBackground,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      textAlign: 'center', fontSize: 7, fontWeight: 400, lineHeight: 1,
                      transform: 'scaleX(var(--pipeline-badge-scale-x, 1))', transformOrigin: 'center',
                    }}
                  >
                    {value}
                  </Box>
                ) : c.key === 'marketRegion' ? (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      fontSize: 'calc(7px / var(--overview-table-scale-x))',
                      transform: 'scaleY(calc(var(--overview-table-scale-x) / var(--overview-table-scale-y)))',
                      transformOrigin: 'center',
                    }}
                  >
                    {value}
                  </Box>
                ) : value}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

const DraggableTable: React.FC<{
  section: PipelineSection;
  x: number;
  y: number;
  width: number;
  height?: number;
  aspectLocked: boolean;
  itemCount: number;
  editable: boolean;
  selected?: boolean;
  onSelect?: () => void;
  onMove?: (key: string, xPct: number, yPct: number) => void;
  onResize?: (key: string, widthPct: number, heightPct: number, xPct?: number, yPct?: number) => void;
  onAspectLockChange?: (key: string, locked: boolean) => void;
  onEditItem?: (itemId: number) => void;
  regionColor?: string;
  onRegionColorChange?: (section: PipelineSection, color?: string) => void;
  launchingSoonColor?: string;
  onLaunchingSoonColorChange?: (color?: string) => void;
  launchingSoonText?: string;
  onLaunchingSoonTextChange?: (text?: string) => void;
  onRenderedHeight?: (section: PipelineSection, height: number) => void;
  blockedAreas?: CanvasArea[];
  children: React.ReactNode;
}> = ({
  section, x, y, width, height, aspectLocked, itemCount, editable,
  selected = false, onSelect,
  onMove, onResize, onAspectLockChange, onEditItem,
  regionColor, onRegionColorChange, launchingSoonColor, onLaunchingSoonColorChange,
  launchingSoonText, onLaunchingSoonTextChange,
  onRenderedHeight, blockedAreas = [], children,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const autoPan = useDeckAutoPan();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState(38 + itemCount * 11);
  const [contextMenu, setContextMenu] = useState<{ top: number; left: number; itemId?: number } | null>(null);
  const drag = useRef<{
    mode: 'move' | 'resize'; pointerX: number; pointerY: number;
    resizeCorner: TableResizeCorner | null;
    x: number; y: number; width: number; height: number; scale: number;
    otherTables: CanvasArea[]; startScrollLeft: number; startScrollTop: number;
  } | null>(null);
  const frame = useRef<number | null>(null);
  const pendingMove = useRef<{ key: string; x: number; y: number } | null>(null);
  const pendingResize = useRef<{ key: string; width: number; height: number; x?: number; y?: number } | null>(null);
  const tableScale = width / TABLE_W;
  const naturalHeight = contentHeight * tableScale;
  const tableHeight = height ?? naturalHeight;
  const tableScaleY = tableHeight / contentHeight;
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const measure = () => setContentHeight(content.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [itemCount]);
  useLayoutEffect(() => {
    onRenderedHeight?.(section, tableHeight);
  }, [onRenderedHeight, section, tableHeight]);
  return (
    <Box
      ref={ref}
      onPointerDown={(event) => {
        const target = event.target as HTMLElement;
        if (editable && event.button === 0) onSelect?.();
        if (
          !editable
          || event.button !== 0
          || target.isContentEditable
          || target.closest('[data-editable-table-cell]')
        ) return;
        const canvas = ref.current?.closest('[data-pipeline-page]') as HTMLElement | null;
        if (!canvas) return;
        const resizeHandle = (event.target as HTMLElement).closest<HTMLElement>('[data-table-resize]');
        const canvasRect = canvas.getBoundingClientRect();
        const scrollHost = ref.current?.closest('[data-deck-scroll]') as HTMLElement | null;
        const scale = canvasRect.width / CANVAS_W;
        const otherTables = Array.from(canvas.querySelectorAll<HTMLElement>('[data-overview-table]'))
          .filter((other) => other !== ref.current)
          .map((other) => {
            const rect = other.getBoundingClientRect();
            return {
              x: (rect.left - canvasRect.left) / scale,
              y: (rect.top - canvasRect.top) / scale,
              w: rect.width / scale,
              h: rect.height / scale,
            };
          });
        drag.current = {
          mode: resizeHandle ? 'resize' : 'move',
          resizeCorner: (resizeHandle?.dataset.corner as TableResizeCorner | undefined) ?? null,
          pointerX: event.clientX, pointerY: event.clientY, x, y, width, height: tableHeight,
          scale, otherTables,
          startScrollLeft: scrollHost?.scrollLeft ?? 0,
          startScrollTop: scrollHost?.scrollTop ?? 0,
        };
        ref.current?.style.setProperty('will-change', 'transform, width, height');
        autoPan.start(event.clientX, event.clientY);
        ref.current?.setPointerCapture(event.pointerId);
        event.preventDefault();
      }}
      onPointerMove={(event) => {
        if (!drag.current || !ref.current) return;
        autoPan.move(event.clientX, event.clientY);
        if (drag.current.mode === 'resize') {
          const scrollHost = ref.current.closest('[data-deck-scroll]') as HTMLElement | null;
          const deltaX = (event.clientX - drag.current.pointerX + (scrollHost?.scrollLeft ?? drag.current.startScrollLeft) - drag.current.startScrollLeft) / drag.current.scale;
          const deltaY = (event.clientY - drag.current.pointerY + (scrollHost?.scrollTop ?? drag.current.startScrollTop) - drag.current.startScrollTop) / drag.current.scale;
          const resizeCorner = drag.current.resizeCorner;
          if (!resizeCorner) return;
          const resizeFromLeft = resizeCorner === 'top-left' || resizeCorner === 'bottom-left';
          const resizeFromTop = resizeCorner === 'top-left' || resizeCorner === 'top-right';
          const oppositeX = drag.current.x + drag.current.width;
          const oppositeY = drag.current.y + drag.current.height;
          const minimumWidth = 300;
          const minimumHeight = 40;
          const maximumWidth = resizeFromLeft ? oppositeX : CANVAS_W - drag.current.x;
          const maximumHeight = resizeFromTop ? oppositeY : CANVAS_H - drag.current.y;
          const candidateForDimensions = (nextWidth: number, nextHeight: number): CanvasArea => ({
            x: resizeFromLeft ? oppositeX - nextWidth : drag.current!.x,
            y: resizeFromTop ? oppositeY - nextHeight : drag.current!.y,
            w: nextWidth,
            h: nextHeight,
          });
          let nextWidth: number;
          let nextHeight: number;
          let nextX = drag.current.x;
          let nextY = drag.current.y;
          const resizeBlocked = (candidate: CanvasArea) => {
            if (!fitsCanvas(candidate)) return true;
            if (blockedAreas.some((area) => areasOverlap(candidate, area, MARKER_COLLISION_PADDING))) return true;
            return drag.current?.otherTables.some((other) => areasOverlap(candidate, other, DEFAULT_TABLE_GAP / 2)) ?? false;
          };
          if (aspectLocked) {
            const widthFactor = (drag.current.width + (resizeFromLeft ? -deltaX : deltaX)) / drag.current.width;
            const heightFactor = (drag.current.height + (resizeFromTop ? -deltaY : deltaY)) / drag.current.height;
            const requestedFactor = Math.abs(widthFactor - 1) >= Math.abs(heightFactor - 1)
              ? widthFactor
              : heightFactor;
            const minimumFactor = Math.max(minimumWidth / drag.current.width, minimumHeight / drag.current.height);
            const maximumFactor = Math.min(maximumWidth / drag.current.width, maximumHeight / drag.current.height);
            if (maximumFactor < minimumFactor) return;
            let factor = Math.min(maximumFactor, Math.max(requestedFactor, minimumFactor));
            const candidateForFactor = (value: number): CanvasArea => {
              const candidateWidth = drag.current!.width * value;
              return candidateForDimensions(candidateWidth, drag.current!.height * value);
            };
            // Move toward the current valid size when the requested corner
            // would hit a protected area. This works for both expansion and
            // contraction from top or bottom corners.
            const step = factor >= 1 ? -0.02 : 0.02;
            while (resizeBlocked(candidateForFactor(factor))
              && Math.abs(factor - 1) > 0.001) {
              const nextFactor = step < 0
                ? Math.max(1, factor + step)
                : Math.min(1, factor + step);
              if (Math.abs(nextFactor - factor) < 0.0001) break;
              factor = nextFactor;
            }
            const proportionalCandidate = candidateForFactor(factor);
            if (resizeBlocked(proportionalCandidate)) return;
            nextX = proportionalCandidate.x;
            nextY = proportionalCandidate.y;
            nextWidth = proportionalCandidate.w;
            nextHeight = proportionalCandidate.h;
          } else {
            nextWidth = Math.min(
              Math.max(drag.current.width + (resizeFromLeft ? -deltaX : deltaX), minimumWidth),
              maximumWidth,
            );
            nextHeight = Math.min(
              Math.max(drag.current.height + (resizeFromTop ? -deltaY : deltaY), minimumHeight),
              maximumHeight,
            );
            const candidate = candidateForDimensions(nextWidth, nextHeight);
            if (resizeBlocked(candidate)) return;
            nextX = candidate.x;
            nextY = candidate.y;
          }
          scheduleFrame(frame, pendingResize, {
            key: `section:${section}`,
            width: nextWidth / CANVAS_W * 100,
            height: nextHeight / CANVAS_H * 100,
            x: nextX / CANVAS_W * 100,
            y: nextY / CANVAS_H * 100,
          }, (next) => onResize?.(next.key, next.width, next.height, next.x, next.y));
          return;
        }
        const scrollHost = ref.current.closest('[data-deck-scroll]') as HTMLElement | null;
        const scrollDeltaX = (scrollHost?.scrollLeft ?? drag.current.startScrollLeft) - drag.current.startScrollLeft;
        const scrollDeltaY = (scrollHost?.scrollTop ?? drag.current.startScrollTop) - drag.current.startScrollTop;
        const nextX = Math.min(Math.max(drag.current.x + (event.clientX - drag.current.pointerX + scrollDeltaX) / drag.current.scale, 0), CANVAS_W - ref.current.offsetWidth);
        const nextY = Math.min(Math.max(drag.current.y + (event.clientY - drag.current.pointerY + scrollDeltaY) / drag.current.scale, 0), CANVAS_H - ref.current.offsetHeight);
        const logicalRect = { x: nextX, y: nextY, w: ref.current.offsetWidth, h: ref.current.offsetHeight };
        const reservedAreas: CanvasArea[] = [
          { x: DEFAULT_LEGEND.x, y: DEFAULT_LEGEND.y, w: DEFAULT_LEGEND.w, h: OVERVIEW_PLACEHOLDER_H },
          { ...OVERVIEW_PLACEHOLDER, h: OVERVIEW_PLACEHOLDER_H },
          ...blockedAreas,
        ];
        if (reservedAreas.some((area) => (
          logicalRect.x < area.x + area.w && logicalRect.x + logicalRect.w > area.x
          && logicalRect.y < area.y + area.h && logicalRect.y + logicalRect.h > area.y
        ))) return;
        const candidate = { x: nextX, y: nextY, w: ref.current.offsetWidth, h: ref.current.offsetHeight };
        if (drag.current.otherTables.some((other) => areasOverlap(candidate, other))) return;
        scheduleFrame(frame, pendingMove, { key: `section:${section}`, x: nextX / CANVAS_W * 100, y: nextY / CANVAS_H * 100 },
          (next) => onMove?.(next.key, next.x, next.y));
      }}
      onPointerUp={() => {
        const current = drag.current;
        autoPan.stop();
        cancelFrame(frame);
        if (current && pendingResize.current) {
          const next = pendingResize.current;
          pendingResize.current = null;
          onResize?.(next.key, next.width, next.height, next.x, next.y);
        } else if (current && pendingMove.current) {
          const next = pendingMove.current;
          pendingMove.current = null;
          onMove?.(next.key, next.x, next.y);
        }
        drag.current = null;
        ref.current?.style.removeProperty('will-change');
      }}
      onPointerCancel={() => {
        autoPan.stop();
        cancelFrame(frame);
        pendingMove.current = null;
        pendingResize.current = null;
        drag.current = null;
        ref.current?.style.removeProperty('will-change');
      }}
      onDoubleClick={(event) => {
        if (!editable) return;
        const itemIdValue = (event.target as HTMLElement).closest<HTMLElement>('[data-pipeline-item-id]')?.dataset.pipelineItemId;
        if (itemIdValue == null) return;
        event.preventDefault();
        event.stopPropagation();
        onEditItem?.(Number(itemIdValue));
      }}
      onContextMenu={(event) => {
        if (!editable) return;
        event.preventDefault();
        event.stopPropagation();
        drag.current = null;
        setContextMenu({
          top: event.clientY,
          left: event.clientX,
        });
      }}
      data-overview-table={section}
      sx={{ position: 'absolute', left: x, top: y, width, height: tableHeight, cursor: editable ? 'grab' : 'default', touchAction: 'none', zIndex: 2 }}
    >
      <Box
        ref={contentRef}
        style={{
          '--pipeline-badge-scale-x': tableScaleY / tableScale,
          '--overview-table-scale-x': tableScale,
          '--overview-table-scale-y': tableScaleY,
        } as React.CSSProperties}
        sx={{ width: TABLE_W, transform: `scale(${tableScale}, ${tableScaleY})`, transformOrigin: 'top left' }}
      >
        {children}
      </Box>
      {editable && selected && (
        <>
          {([
            ['top-left', { left: -4, top: -4, cursor: 'nwse-resize' }],
            ['top-right', { right: -4, top: -4, cursor: 'nesw-resize' }],
            ['bottom-left', { left: -4, bottom: -4, cursor: 'nesw-resize' }],
            ['bottom-right', { right: -4, bottom: -4, cursor: 'nwse-resize' }],
          ] as Array<[TableResizeCorner, { left?: number; right?: number; top?: number; bottom?: number; cursor: string }]>).map(([corner, position]) => (
            <Box
              key={corner}
              data-table-resize
              data-corner={corner}
              title={aspectLocked ? 'Drag to resize table proportionally' : 'Drag to resize table freely'}
              sx={{
                position: 'absolute', width: 11, height: 11, ...position,
                bgcolor: '#ffffff', border: '2px solid #1f2a44', borderRadius: '50%',
                boxSizing: 'border-box',
                cursor: position.cursor, boxShadow: '0 1px 3px rgba(0,0,0,0.35)', zIndex: 3,
              }}
            />
          ))}
          <Menu
            open={contextMenu != null}
            onClose={() => setContextMenu(null)}
            onPointerDown={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.stopPropagation()}
            anchorReference="anchorPosition"
            anchorPosition={contextMenu ?? undefined}
            MenuListProps={{ dense: true, 'aria-label': 'Table options' }}
          >
            <MenuItem onClick={(event) => event.stopPropagation()} sx={{ gap: 1.5 }}>
              <Box
                component="input"
                type="color"
                aria-label={`Choose ${SECTION_LABELS[section]} header colour`}
                value={regionColor ?? SECTION_COLORS[section].bg}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onRegionColorChange?.(section, event.target.value);
                }}
                sx={{ width: 34, height: 26, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
              />
              Choose region colour
            </MenuItem>
            <MenuItem
              disabled={!regionColor}
              onClick={() => {
                onRegionColorChange?.(section, undefined);
                setContextMenu(null);
              }}
            >
              Reset region colour
            </MenuItem>
            <MenuItem onClick={(event) => event.stopPropagation()} sx={{ gap: 1.5 }}>
              <Box
                component="input"
                type="color"
                aria-label="Choose launching soon colour"
                value={launchingSoonColor ?? '#111111'}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onLaunchingSoonColorChange?.(event.target.value)}
                sx={{ width: 34, height: 26, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
              />
              Launching soon colour
            </MenuItem>
            <MenuItem onClick={(event) => event.stopPropagation()} sx={{ display: 'block' }}>
              <TextField
                size="small"
                fullWidth
                label="Launching soon label"
                value={launchingSoonText ?? ': Launching Soon!'}
                onChange={(event) => onLaunchingSoonTextChange?.(event.target.value)}
                onClick={(event) => event.stopPropagation()}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                onAspectLockChange?.(`section:${section}`, !aspectLocked);
                setContextMenu(null);
              }}
            >
              {aspectLocked ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

/** Editable transparent callout box used to outline launching-soon projects. */
const DraggableLaunchingSoonBox: React.FC<{
  boxKey: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  editable?: boolean;
  onChange?: (x: number, y: number, width: number, height: number) => void;
  onContextMenu?: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ x, y, width, height, color, editable = false, onChange, onContextMenu }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const autoPan = useDeckAutoPan();
  const frame = useRef<number | null>(null);
  const pending = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const drag = useRef<{
    mode: 'move' | 'resize'; px: number; py: number; x: number; y: number;
    width: number; height: number; scale: number; startScrollLeft: number; startScrollTop: number;
  } | null>(null);
  return (
    <Box
      ref={ref}
      data-launching-soon-box
      sx={{
        position: 'absolute', left: x, top: y, width, height, boxSizing: 'border-box',
        border: `3px solid ${color}`, backgroundColor: 'transparent',
        zIndex: 4, cursor: editable ? 'move' : 'default', touchAction: 'none',
      }}
      onContextMenu={(event) => {
        if (!editable) return;
        event.preventDefault();
        event.stopPropagation();
        onContextMenu?.(event);
      }}
      onPointerDown={(event) => {
        if (!editable || event.button !== 0) return;
        const canvas = ref.current?.closest('[data-pipeline-page]') as HTMLElement | null;
        if (!canvas) return;
        const scrollHost = ref.current?.closest('[data-deck-scroll]') as HTMLElement | null;
        drag.current = {
          mode: (event.target as HTMLElement).closest('[data-launching-soon-box-resize]') ? 'resize' : 'move',
          px: event.clientX, py: event.clientY, x, y, width, height,
          scale: canvas.getBoundingClientRect().width / CANVAS_W,
          startScrollLeft: scrollHost?.scrollLeft ?? 0,
          startScrollTop: scrollHost?.scrollTop ?? 0,
        };
        ref.current?.style.setProperty('will-change', 'transform, width, height');
        autoPan.start(event.clientX, event.clientY);
        ref.current?.setPointerCapture(event.pointerId);
        event.preventDefault();
      }}
      onPointerMove={(event) => {
        if (!drag.current) return;
        autoPan.move(event.clientX, event.clientY);
        const current = drag.current;
        const scrollHost = ref.current?.closest('[data-deck-scroll]') as HTMLElement | null;
        const dx = (event.clientX - current.px + (scrollHost?.scrollLeft ?? current.startScrollLeft) - current.startScrollLeft) / current.scale;
        const dy = (event.clientY - current.py + (scrollHost?.scrollTop ?? current.startScrollTop) - current.startScrollTop) / current.scale;
        if (current.mode === 'resize') {
          scheduleFrame(frame, pending, {
            x: current.x,
            y: current.y,
            width: Math.min(Math.max(current.width + dx, 40), CANVAS_W - current.x),
            height: Math.min(Math.max(current.height + dy, 40), CANVAS_H - current.y),
          }, (next) => onChange?.(
            current.x,
            current.y,
            next.width,
            next.height,
          ));
          return;
        }
        scheduleFrame(frame, pending, {
          x: Math.min(Math.max(current.x + dx, 0), CANVAS_W - current.width),
          y: Math.min(Math.max(current.y + dy, 0), CANVAS_H - current.height),
          width: current.width,
          height: current.height,
        }, (next) => onChange?.(next.x, next.y, next.width, next.height));
      }}
      onPointerUp={(event) => {
        autoPan.stop();
        if (ref.current?.hasPointerCapture(event.pointerId)) ref.current.releasePointerCapture(event.pointerId);
        cancelFrame(frame);
        if (pending.current) {
          const next = pending.current;
          pending.current = null;
          onChange?.(next.x, next.y, next.width, next.height);
        }
        drag.current = null;
        ref.current?.style.removeProperty('will-change');
      }}
      onPointerCancel={(event) => {
        autoPan.stop();
        if (ref.current?.hasPointerCapture(event.pointerId)) ref.current.releasePointerCapture(event.pointerId);
        cancelFrame(frame);
        pending.current = null;
        drag.current = null;
        ref.current?.style.removeProperty('will-change');
      }}
    >
      {editable && (
        <Box
          data-launching-soon-box-resize
          title="Drag to resize launching soon box"
          sx={{
            position: 'absolute', right: -7, bottom: -7, width: 14, height: 14,
            bgcolor: '#ffffff', border: '2px solid #1f2a44', borderRadius: '2px',
            cursor: 'nwse-resize', boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
          }}
        />
      )}
    </Box>
  );
};

interface OverviewPageProps {
  items: PipelineItem[];
  layout?: PageLayout;
  asAtDate: string;
  fixed?: boolean;
  editable?: boolean;
  onItemChange?: (item: PipelineItem) => void;
  onTableMove?: (key: string, xPct: number, yPct: number) => void;
  onTableResize?: (key: string, widthPct: number, heightPct: number, xPct?: number, yPct?: number) => void;
  onColumnWidthsChange?: (key: string, widths: Record<string, number>) => void;
  onTableAspectLockChange?: (key: string, locked: boolean) => void;
  onRegionColorChange?: (section: PipelineSection, color?: string) => void;
  onLegendChange?: (xPct: number, yPct: number, widthPct: number) => void;
  onLegendCaptionChange?: (xPct: number, yPct: number, widthPct: number) => void;
  onLaunchingSoonBoxChange?: (boxKey: string, xPct: number, yPct: number, widthPct: number, heightPct: number) => void;
  onLaunchingSoonBoxDelete?: (boxKey: string) => void;
  onLaunchingSoonColorChange?: (color?: string) => void;
  onLaunchingSoonTextChange?: (text?: string) => void;
  backgroundImage?: string;
  renderMode?: DeckRenderMode;
}

/** Page 2 of the deck: full table columns left/right with the numbered map centre. */
export const OverviewPage: React.FC<OverviewPageProps> = ({
  items, layout = {}, asAtDate, fixed = false, editable = false,
  onItemChange, onTableMove, onTableResize, onColumnWidthsChange,
  onTableAspectLockChange, onLegendChange,
  onRegionColorChange, onLegendCaptionChange, onLaunchingSoonBoxChange,
  onLaunchingSoonBoxDelete,
  onLaunchingSoonColorChange, onLaunchingSoonTextChange,
  backgroundImage,
  renderMode = 'interactive',
}) => {
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [selectedTableKey, setSelectedTableKey] = useState<string | null>(null);
  const [liveColumnWidths, setLiveColumnWidths] = useState<Record<string, number> | null>(null);
  const [launchingSoonContextMenu, setLaunchingSoonContextMenu] = useState<{ boxKey: string; top: number; left: number } | null>(null);
  const [renderedTableHeights, setRenderedTableHeights] = useState<Partial<Record<PipelineSection, number>>>({});
  const rememberRenderedHeight = React.useCallback((section: PipelineSection, height: number) => {
    setRenderedTableHeights((current) => Math.abs((current[section] ?? 0) - height) < 0.5
      ? current
      : { ...current, [section]: height });
  }, []);
  const numbers = useMemo(() => {
    const result = new Map<number, number>();
    let next = 1;
    SECTION_ORDER.forEach((section) => {
      items
        .filter((item) => item.section === section)
        .sort(comparePipelineItems)
        .forEach((item) => result.set(item.id, next++));
    });
    return result;
  }, [items]);
  const markers = useMemo<OverviewMarker[]>(() => items.flatMap((item) => {
    if (!hasFiniteCoordinates(item.latitude, item.longitude)) return [];
    const point = projectToCanvas(MAP_RECT, Number(item.longitude), Number(item.latitude));
    // Numbering is presentation metadata only; a valid coordinate always
    // receives a marker even if an item is not present in the ordering map.
    return [{ item, number: numbers.get(item.id) ?? 0, ...point }];
  }), [items, numbers]);

  // Cluster only nearby projects (maximum three per marker), allowing circles
  // to overlap while nudging the marker only when its number would be hidden.
  const positionedMarkers = useMemo<PositionedOverviewMarker[]>(() => {
    const bounds = {
      left: MAP_RECT.x,
      right: MAP_RECT.x + MAP_RECT.w,
      top: MAP_RECT.y,
      bottom: MAP_RECT.y + mapRectHeight(MAP_RECT),
    };
    const clamp = (marker: PositionedOverviewMarker): PositionedOverviewMarker => ({
      ...marker,
      x: Math.max(bounds.left + marker.radius, Math.min(bounds.right - marker.radius, marker.x)),
      y: Math.max(bounds.top + marker.radius, Math.min(bounds.bottom - marker.radius, marker.y)),
    });
    const clustered = clusterMarkerPlacements(markers.map((marker) => ({
      itemId: marker.item.id,
      number: marker.number,
      x: marker.x,
      y: marker.y,
      radius: PROJECT_MARKER_RADIUS,
    })));
    const resolved = resolveMarkerTextCollisions(
      clustered.map((marker) => ({
        ...marker,
        // The shared clustering helper may enlarge dense clusters; Overview
        // markers intentionally remain a fixed 8px radius in every case.
        radius: PROJECT_MARKER_RADIUS,
        item: markers.find((candidate) => candidate.item.id === marker.itemIds[0])?.item ?? markers[0].item,
      })),
      { left: MAP_RECT.x, right: MAP_RECT.x + MAP_RECT.w, top: MAP_RECT.y, bottom: MAP_RECT.y + mapRectHeight(MAP_RECT) },
    );
    return resolved.map((marker) => clamp(marker));
  }, [markers]);
  const blockedTableAreas = useMemo<CanvasArea[]>(() => [
    OVERVIEW_TITLE_BLOCKER,
    ...positionedMarkers.map((marker) => ({
      x: marker.x - marker.radius,
      y: marker.y - marker.radius,
      w: marker.radius * 2,
      h: marker.radius * 2,
    })),
  ], [positionedMarkers]);
  const bySection = (section: PipelineSection) =>
    items
      .filter((i) => section === 'COMMERCIAL_INDUSTRIAL'
        ? i.section === 'COMMERCIAL' || i.section === 'INDUSTRIAL' || i.section === 'COMMERCIAL_INDUSTRIAL'
        : i.section === section)
      .sort(comparePipelineItems);
  // Widths are now shared by every Overview table. Older saved layouts may
  // still have section-specific maps, so use the first legacy map only when
  // the shared map has not yet been written.
  const overviewLayout = layout as OverviewLayout;
  const persistedColumnWidths = overviewLayout.columnWidths ?? SECTION_ORDER
    .map((section) => overviewLayout[`section:${section}`]?.columnWidths)
    .find((widths): widths is Record<string, number> => widths != null);
  const normalizedSharedColumnWidths = useMemo(() => {
    const columns = resolveOverviewColumns(items, numbers, persistedColumnWidths);
    return Object.fromEntries(columns.map((column) => [String(column.key), column.w / TABLE_W * 100]));
  }, [items, numbers, persistedColumnWidths]);
  const sharedColumnWidths = liveColumnWidths ?? normalizedSharedColumnWidths;
  const previewColumnWidths = React.useCallback((widths: Record<string, number>) => {
    setLiveColumnWidths(widths);
  }, []);
  const commitColumnWidths = React.useCallback((widths: Record<string, number>) => {
    setLiveColumnWidths(null);
    onColumnWidthsChange?.('overview', widths);
  }, [onColumnWidthsChange]);
  const cancelColumnWidths = React.useCallback(() => {
    setLiveColumnWidths(null);
  }, []);
  const savedLegend = layout.legend;
  const launchingSoonColor = savedLegend?.launchingSoonColor;
  const launchingSoonText = savedLegend?.launchingSoonText;
  const legend = {
    x: savedLegend?.x != null ? savedLegend.x / 100 * CANVAS_W : DEFAULT_LEGEND.x,
    y: savedLegend?.y != null ? savedLegend.y / 100 * CANVAS_H : DEFAULT_LEGEND.y,
    w: savedLegend?.width != null ? savedLegend.width / 100 * CANVAS_W : DEFAULT_LEGEND.w,
  };
  const furnitureBlockedAreas: CanvasArea[] = [
    { x: legend.x, y: legend.y, w: legend.w, h: mapLegendHeight(legend.w, true) },
    { ...OVERVIEW_PLACEHOLDER, h: OVERVIEW_PLACEHOLDER_H },
  ];
  const caption = {
    x: layout['legend-caption']?.x != null ? layout['legend-caption'].x / 100 * CANVAS_W : DEFAULT_CAPTION.x,
    y: layout['legend-caption']?.y != null
      ? layout['legend-caption'].y / 100 * CANVAS_H
      : DEFAULT_CAPTION.y,
    w: layout['legend-caption']?.width != null
      ? layout['legend-caption'].width / 100 * CANVAS_W
      : DEFAULT_CAPTION_WIDTH,
  };
  const launchingSoonBoxes = Object.entries(layout)
    .filter(([key, value]) => (
      (key === 'launching-soon-box' || /^launching-soon-box:\d+$/.test(key))
      && value?.x != null && value?.y != null
    ))
    .map(([boxKey, saved]) => ({
      boxKey,
      x: saved.x! / 100 * CANVAS_W,
      y: saved.y! / 100 * CANVAS_H,
      width: Math.max(40, (saved.width ?? 14) / 100 * CANVAS_W),
      height: Math.max(40, (saved.height ?? 14) / 100 * CANVAS_H),
    }));

  return (
    <DeckPage fixed={fixed} pageKey="overview" background="main" backgroundImage={backgroundImage} renderMode={renderMode}>
      {/* map + project markers behind the tables (the enlarged island tucks under them) */}
      <svg
        width={CANVAS_W}
        height={CANVAS_H}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <DeckMap rect={MAP_RECT} mainlandOnly />
        {positionedMarkers.map((marker) => {
          const section = marker.item.section;
            const markerColor = regionColorOf(layout, section) ?? SECTION_COLORS[section].bg;
          const markerNumbers = (marker.numbers ?? [marker.number]).slice().sort((a, b) => a - b);
          const markerText = markerTextLayout({ ...marker, numbers: markerNumbers });
          const { lines: markerLines, fontSize: markerFontSize, lineHeight: markerLineHeight } = markerText;
          const clusterColors = (marker.itemIds ?? [marker.item.id]).reduce<string[]>((colors, itemId) => {
            const itemForMarker = items.find((candidate) => candidate.id === itemId);
            if (!itemForMarker) return colors;
            const color = regionColorOf(layout, itemForMarker.section) ?? SECTION_COLORS[itemForMarker.section].bg;
            return colors.includes(color) ? colors : [...colors, color];
          }, []);
          const segmentColors = clusterColors.length ? clusterColors : [markerColor];
          const circumference = 2 * Math.PI * marker.radius;
          return (
            <g key={marker.item.id}>
              <circle cx={marker.x} cy={marker.y} r={marker.radius} fill="#ffffff" stroke="#ffffff" strokeWidth={1.8} />
              {segmentColors.map((color, index) => {
                const count = (marker.itemIds ?? [marker.item.id]).filter((itemId) => {
                  const itemForMarker = items.find((candidate) => candidate.id === itemId);
                  if (!itemForMarker) return false;
                  const itemColor = regionColorOf(layout, itemForMarker.section) ?? SECTION_COLORS[itemForMarker.section].bg;
                  return itemColor === color;
                }).length;
                const portion = count / Math.max(1, marker.itemIds?.length ?? 1);
                const offset = segmentColors.slice(0, index).reduce((sum, previousColor) => {
                  const previousCount = (marker.itemIds ?? [marker.item.id]).filter((itemId) => {
                    const itemForMarker = items.find((candidate) => candidate.id === itemId);
                    if (!itemForMarker) return false;
                    const itemColor = regionColorOf(layout, itemForMarker.section) ?? SECTION_COLORS[itemForMarker.section].bg;
                    return itemColor === previousColor;
                  }).length;
                  return sum + previousCount / Math.max(1, marker.itemIds?.length ?? 1) * circumference;
                }, 0);
                return <circle key={`${marker.item.id}-segment-${index}`} cx={marker.x} cy={marker.y} r={marker.radius} fill="none" stroke={color} strokeWidth={1.8} strokeDasharray={`${portion * circumference} ${circumference}`} strokeDashoffset={-offset} transform={`rotate(-90 ${marker.x} ${marker.y})`} />;
              })}
              {marker.number > 0 && (
                <text
                  x={marker.x}
                  y={marker.y - ((markerLines.length - 1) * markerLineHeight) / 2 + markerFontSize * 0.35}
                  fontSize={markerFontSize}
                  fontWeight={800}
                  fill="#000000"
                  stroke="#ffffff"
                  strokeWidth={0.45}
                  paintOrder="stroke"
                  textAnchor="middle"
                >
                  {markerLines.map((line, index) => (
                    <tspan key={`${marker.item.id}-marker-line-${index}`} x={marker.x} dy={index === 0 ? 0 : markerLineHeight}>
                      {line}
                    </tspan>
                  ))}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {(() => {
        const occupied: CanvasArea[] = [];
        return [LEFT_SECTIONS, RIGHT_SECTIONS].flatMap((sections, column) => {
          let nextY = 10;
          return sections.map((section) => {
            const sectionItems = bySection(section);
            if (!sectionItems.length) return null;
            const saved = layout[`section:${section}`];
            const regionColor = regionColorOf(layout, section);
            const width = saved?.width != null ? saved.width / 100 * CANVAS_W : TABLE_W;
            const savedHeight = saved?.height != null ? saved.height / 100 * CANVAS_H : undefined;
            const baseNaturalHeight = 38 + sectionItems.length * 11;
            const naturalDefaultHeight = baseNaturalHeight * (width / TABLE_W);
            const maxDefaultHeight = Math.max(40, Math.min(naturalDefaultHeight, CANVAS_H - 20));
            const preferredX = saved?.x != null ? saved.x / 100 * CANVAS_W : column === 0 ? 10 : CANVAS_W - width - 10;
            const preferredY = saved?.y != null ? saved.y / 100 * CANVAS_H : nextY;
            const preferred = { x: preferredX, y: preferredY, w: width, h: savedHeight ?? maxDefaultHeight };
            const safe = saved?.x != null || saved?.y != null
              ? preferred
              : findSafeTablePosition(preferred, [
                ...furnitureBlockedAreas,
                ...blockedTableAreas,
              ], occupied);
            const x = safe.x;
            const y = safe.y;
            const height = savedHeight ?? (safe.h < preferred.h
              ? safe.h
              : naturalDefaultHeight > maxDefaultHeight ? maxDefaultHeight : undefined);
            const renderedHeight = height ?? renderedTableHeights[section] ?? naturalDefaultHeight;
            occupied.push({ x, y, w: width, h: renderedHeight });
            // A wider table scales vertically as well. Stack from its rendered
            // height so the intended gutter is not consumed by that scaling.
            // Explicitly saved positions remain authoritative.
            nextY = saved?.y != null
              ? Math.max(nextY, y + renderedHeight + DEFAULT_TABLE_GAP)
              : y + renderedHeight + DEFAULT_TABLE_GAP;
            return (
              <DraggableTable
                key={section}
                section={section}
                x={x}
                y={y}
                width={width}
                height={height}
                aspectLocked={saved?.aspectLocked ?? true}
                itemCount={sectionItems.length}
                editable={editable && !fixed}
                selected={selectedTableKey === `section:${section}`}
                onSelect={() => setSelectedTableKey(`section:${section}`)}
                onMove={onTableMove}
                onResize={onTableResize}
                onAspectLockChange={onTableAspectLockChange}
                onEditItem={setEditingItemId}
                regionColor={regionColor}
                onRegionColorChange={onRegionColorChange}
                launchingSoonColor={launchingSoonColor}
                onLaunchingSoonColorChange={onLaunchingSoonColorChange}
                launchingSoonText={launchingSoonText}
                onLaunchingSoonTextChange={onLaunchingSoonTextChange}
                onRenderedHeight={rememberRenderedHeight}
                blockedAreas={[...furnitureBlockedAreas, ...blockedTableAreas]}
              >
                <SectionTable
                  section={section}
                  items={sectionItems}
                  numbers={numbers}
                  editable={editable && !fixed}
                  editingItemId={editingItemId}
                  onItemChange={onItemChange}
                  onEditItem={setEditingItemId}
                  headerColor={regionColor}
                  savedColumnWidths={sharedColumnWidths}
                  sharedColumnWidths={sharedColumnWidths}
                  onColumnWidthsChange={previewColumnWidths}
                  onColumnWidthsCommit={commitColumnWidths}
                  onColumnWidthsCancel={cancelColumnWidths}
                />
              </DraggableTable>
            );
          });
        });
      })()}

      {/* centre title — sits below the logo baked into the background artwork */}
      <Box sx={{ position: 'absolute', left: OVERVIEW_TITLE_LAYOUT_RECT.x, top: OVERVIEW_TITLE_LAYOUT_RECT.y, width: OVERVIEW_TITLE_LAYOUT_RECT.w, textAlign: 'center' }}>
        <Box
          sx={{
            fontSize: 52,
            fontWeight: 900,
            color: '#ffffff',
            letterSpacing: 2,
            lineHeight: 1.05,
            textShadow: '0 3px 8px rgba(0,0,0,0.5)',
          }}
        >
          PROJECTS IN
          <br />
          THE PIPELINE
        </Box>
      </Box>

      {/* foreground furniture: compact legend beside the white insert box */}
      <svg
        width={CANVAS_W}
        height={CANVAS_H}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <rect
          x={OVERVIEW_PLACEHOLDER.x}
          y={OVERVIEW_PLACEHOLDER.y}
          width={OVERVIEW_PLACEHOLDER.w}
          height={OVERVIEW_PLACEHOLDER_H}
          rx={4}
          fill="#ffffff"
          stroke="#c7ccd4"
        />
      </svg>

      <AdjustableMapLegend
        x={legend.x}
        y={legend.y}
        w={legend.w}
        editable={editable && !fixed}
        includeDeckKey
        launchingSoonColor={launchingSoonColor}
        launchingSoonText={launchingSoonText}
        onLaunchingSoonColorChange={onLaunchingSoonColorChange}
        onLaunchingSoonTextChange={onLaunchingSoonTextChange}
        onChange={(x, y, width) => onLegendChange?.(
          x / CANVAS_W * 100,
          y / CANVAS_H * 100,
          width / CANVAS_W * 100,
        )}
      />

      <DeckLegendCaption
        x={caption.x}
        y={caption.y}
        width={caption.w}
        asAtDate={asAtDate}
        editable={editable && !fixed}
        onChange={(x, y, width) => onLegendCaptionChange?.(
          x / CANVAS_W * 100,
          y / CANVAS_H * 100,
          width / CANVAS_W * 100,
        )}
      />

      {launchingSoonBoxes.map((box) => (
        <DraggableLaunchingSoonBox
          key={box.boxKey}
          {...box}
          color={launchingSoonColor ?? '#111111'}
          editable={editable && !fixed}
          onChange={(x, y, width, height) => onLaunchingSoonBoxChange?.(
            box.boxKey,
            x / CANVAS_W * 100,
            y / CANVAS_H * 100,
            width / CANVAS_W * 100,
            height / CANVAS_H * 100,
          )}
          onContextMenu={(event) => setLaunchingSoonContextMenu({
            boxKey: box.boxKey,
            top: event.clientY,
            left: event.clientX,
          })}
        />
      ))}

      <Menu
        open={launchingSoonContextMenu != null}
        onClose={() => setLaunchingSoonContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={launchingSoonContextMenu ? { top: launchingSoonContextMenu.top, left: launchingSoonContextMenu.left } : undefined}
      >
        <MenuItem
          onClick={() => {
            if (launchingSoonContextMenu) onLaunchingSoonBoxDelete?.(launchingSoonContextMenu.boxKey);
            setLaunchingSoonContextMenu(null);
          }}
        >
          Delete launching soon box
        </MenuItem>
      </Menu>

      <DeckFooter asAtDate={asAtDate} />
    </DeckPage>
  );
};
